import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { AlertCircle, CheckCircle, LogOut, Moon, Sun, Bug, ArrowLeft } from 'lucide-react';
import { COGNITO_FORMS_CONFIG, FORM_FIELD_NAMES } from './config';

const EntraIDFormAuth = () => {
  const [authState, setAuthState] = useState('loading'); // loading, unauthenticated, authenticating, authenticated, blocked
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to dark
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? saved === 'true' : true;
  });
  const [debugMode, setDebugMode] = useState(() => {
    // Check localStorage for saved preference, default to false
    const saved = localStorage.getItem('debugMode');
    return saved !== null ? saved === 'true' : false;
  });
  const [msalInstance] = useState(() => new PublicClientApplication(msalConfig));
  const formContainerRef = useRef(null);
  const [formLoaded, setFormLoaded] = useState(false);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Save debug mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('debugMode', debugMode);
  }, [debugMode]);

  // Check if tenant is allowed via server-side API (sends access token for validation)
  const isTenantAllowed = useCallback(async (tenantId) => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return false;

      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: accounts[0]
      });

      const response = await fetch('/api/validate-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-MS-Token': tokenResponse.accessToken,
        },
        body: JSON.stringify({ tenantId }),
      });

      if (!response.ok) {
        console.error('Tenant validation failed:', response.status);
        return false;
      }

      const data = await response.json();
      return data.authorized === true;
    } catch (error) {
      console.error('Error calling tenant validation API:', error);
      return false;
    }
  }, [msalInstance]);

  // Initialize MSAL and handle authentication on mount
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // Handle redirect response first
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          // User just returned from login redirect
          const tenantId = response.account.tenantId;

          const allowed = await isTenantAllowed(tenantId);
          if (!allowed) {
            setUser({
              name: response.account.name,
              email: response.account.username,
              userPrincipalName: response.account.username,
              tenantId: response.account.tenantId,
              userId: response.account.homeAccountId
            });
            setAuthState('blocked');
            setError(`Access denied. Your organization (${tenantId}) is not authorized to use this form.`);
            return;
          }

          setUser({
            name: response.account.name,
            email: response.account.username,
            userPrincipalName: response.account.username,
            tenantId: response.account.tenantId,
            userId: response.account.homeAccountId
          });
          setAuthState('authenticated');
        } else {
          // Check if user is already signed in (session exists)
          const accounts = msalInstance.getAllAccounts();

          if (accounts.length > 0) {
            const account = accounts[0];
            const tenantId = account.tenantId;

            const allowed = await isTenantAllowed(tenantId);
            if (!allowed) {
              setUser({
                name: account.name,
                email: account.username,
                userPrincipalName: account.username,
                tenantId: account.tenantId,
                userId: account.homeAccountId
              });
              setAuthState('blocked');
              setError(`Access denied. Your organization (${tenantId}) is not authorized to use this form.`);
              return;
            }

            setUser({
              name: account.name,
              email: account.username,
              userPrincipalName: account.username,
              tenantId: account.tenantId,
              userId: account.homeAccountId
            });
            setAuthState('authenticated');
          } else {
            setAuthState('unauthenticated');
          }
        }
      } catch (err) {
        console.error('Initialization error:', err);
        setError('Failed to initialize authentication. Please refresh the page.');
        setAuthState('unauthenticated');
      }
    };

    initializeMsal();
  }, [msalInstance, isTenantAllowed]);

  // Real MSAL authentication - redirects to Microsoft login
  const handleLogin = async () => {
    setAuthState('authenticating');
    setError(null);

    try {
      await msalInstance.loginRedirect({
        scopes: ['User.Read'],
        prompt: 'select_account'
      });
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Authentication failed. Please try again.');
      setAuthState('unauthenticated');
    }
  };

  const handleLogout = async () => {
    try {
      await msalInstance.logoutRedirect();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Acquire token silently to keep session active
  const acquireTokenSilently = useCallback(async () => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return;

      await msalInstance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: accounts[0]
      });
    } catch (err) {
      console.warn('Silent token acquisition failed:', err);
      // If silent acquisition fails, user will need to re-authenticate on next action
    }
  }, [msalInstance]);

  // Refresh token periodically to prevent expiration
  useEffect(() => {
    if (authState === 'authenticated') {
      // Refresh token every 30 minutes (tokens typically expire after 1 hour)
      const interval = setInterval(() => {
        acquireTokenSilently();
      }, 30 * 60 * 1000); // 30 minutes

      return () => clearInterval(interval);
    }
  }, [authState, acquireTokenSilently]);

  // Load Cognito Forms seamless script and prefill form when user is authenticated
  useEffect(() => {
    if (authState === 'authenticated' && user && !formLoaded) {
      // Load the Cognito Forms seamless.js script
      const script = document.createElement('script');
      script.src = 'https://www.cognitoforms.com/f/seamless.js';
      script.setAttribute('data-key', COGNITO_FORMS_CONFIG.dataKey);
      script.setAttribute('data-form', COGNITO_FORMS_CONFIG.dataForm);

      script.onload = () => {
        setFormLoaded(true);

        // Wait a moment for Cognito object to be available
        setTimeout(() => {
          if (window.Cognito && window.Cognito.prefill) {
            // Prepare prefill data based on field mappings
            const prefillData = {};

            if (FORM_FIELD_NAMES.userEmail) {
              prefillData[FORM_FIELD_NAMES.userEmail] = user.email;
            }
            if (FORM_FIELD_NAMES.userEmailForReceipt) {
              prefillData[FORM_FIELD_NAMES.userEmailForReceipt] = user.email;
            }
            if (FORM_FIELD_NAMES.userName && user.name) {
              // Parse the name into First and Last components
              // Microsoft returns names in various formats: "Last, First" or "First Last"
              let firstName = '';
              let lastName = '';

              if (user.name.includes(',')) {
                // Format: "Last, First"
                const parts = user.name.split(',').map(p => p.trim());
                lastName = parts[0] || '';
                firstName = parts[1] || '';
              } else {
                // Format: "First Last" or just "First"
                const parts = user.name.split(' ');
                firstName = parts[0] || '';
                lastName = parts.slice(1).join(' ') || '';
              }

              // Cognito Forms expects a Name object with First and Last properties
              prefillData[FORM_FIELD_NAMES.userName] = {
                First: firstName,
                Last: lastName
              };
            }
            if (FORM_FIELD_NAMES.userPrincipalName) {
              prefillData[FORM_FIELD_NAMES.userPrincipalName] = user.userPrincipalName;
            }
            if (FORM_FIELD_NAMES.tenantId) {
              prefillData[FORM_FIELD_NAMES.tenantId] = user.tenantId;
            }
            if (FORM_FIELD_NAMES.userId) {
              prefillData[FORM_FIELD_NAMES.userId] = user.userId;
            }

            // Prefill the form using Cognito Forms API
            window.Cognito.prefill(prefillData);
          }
        }, 500);
      };

      if (formContainerRef.current) {
        formContainerRef.current.appendChild(script);
      }

      return () => {
        // Cleanup: remove script when component unmounts
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, [authState, user, formLoaded]);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-zinc-900 via-neutral-900 to-stone-900' : 'bg-gradient-to-br from-cyan-50 to-blue-50'} flex items-center justify-center p-4 transition-colors duration-300`}>
      {/* Toggle Buttons */}
      <div className="fixed top-4 right-4 flex gap-2 z-50">
        {/* Debug Toggle Button */}
        <button
          onClick={() => setDebugMode(!debugMode)}
          className={`p-3 rounded-full ${debugMode ? (darkMode ? 'bg-cyan-600 border-cyan-500 text-white' : 'bg-[#00637d] border-[#005670] text-white') : (darkMode ? 'bg-stone-800/80 backdrop-blur border border-stone-700/50 text-gray-400 hover:bg-stone-700/80' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100')} shadow-lg transition-all`}
          aria-label="Toggle debug mode"
          title={debugMode ? "Hide debug info" : "Show debug info"}
        >
          <Bug className="w-5 h-5" />
        </button>

        {/* Theme Toggle Button */}
        <button
          onClick={() => setDarkMode(!darkMode)}
          className={`p-3 rounded-full ${darkMode ? 'bg-stone-800/80 backdrop-blur border border-stone-700/50 text-cyan-400 hover:bg-stone-700/80' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'} shadow-lg transition-all`}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      <div className="max-w-4xl w-full">

        {/* Loading State */}
        {authState === 'loading' && (
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-2xl p-12 text-center transition-colors duration-300`}>
            <div className="animate-spin w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>Loading...</h2>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Initializing authentication</p>
          </div>
        )}

        {/* Unauthenticated State - Login */}
        {authState === 'unauthenticated' && (
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-2xl p-8 text-center transition-colors duration-300`}>
            <div className="mb-8">
              <div className={`w-16 h-16 ${darkMode ? 'bg-gradient-to-br from-cyan-600 to-cyan-700' : 'bg-[#00637d]'} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                </svg>
              </div>
              <h1 className={`text-3xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-3`}>User Onboarding Form</h1>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sign in with your organization's Microsoft 365 account.</p>
            </div>

            {error && (
              <div className={`mb-6 p-4 ${darkMode ? 'bg-red-950 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg flex items-start gap-3`}>
                <AlertCircle className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'} flex-shrink-0 mt-0.5`} />
                <p className={`${darkMode ? 'text-red-300' : 'text-red-800'} text-sm`}>{error}</p>
              </div>
            )}

            <button
              onClick={handleLogin}
              className={`${darkMode ? 'bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800' : 'bg-[#00637d] hover:bg-[#005670]'} text-white font-semibold py-3 px-8 rounded-lg transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2 mb-6`}
            >
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                <path d="M0 0h10.931v10.931H0V0zm12.069 0H23v10.931H12.069V0zM0 12.069h10.931V23H0V12.069zm12.069 0H23V23H12.069V12.069z"/>
              </svg>
              Sign in with Microsoft
            </button>

            <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>This form is available to verified Ascend Nonprofit Solutions customers.</p>

          </div>
        )}

        {/* Blocked State - Access Denied */}
        {authState === 'blocked' && (
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-2xl p-8 transition-colors duration-300`}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h1 className={`text-3xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>Access Denied</h1>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>Your organization is not authorized to access this form.</p>
            </div>

            {error && (
              <div className={`mb-6 p-4 ${darkMode ? 'bg-red-950 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg`}>
                <p className={`${darkMode ? 'text-red-300' : 'text-red-800'} text-sm`}>{error}</p>
              </div>
            )}

            {user && (
              <div className={`mb-6 p-4 ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg`}>
                <h4 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>Authenticated User:</h4>
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} space-y-1`}>
                  <div><span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name:</span> {user.name}</div>
                  <div><span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email:</span> {user.email}</div>
                  <div><span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tenant ID:</span> {user.tenantId}</div>
                </div>
              </div>
            )}

            <div className={`${darkMode ? 'bg-amber-950 border-amber-800' : 'bg-amber-50 border-amber-200'} border rounded-lg p-6 mb-6`}>
              <h3 className={`font-semibold ${darkMode ? 'text-amber-200' : 'text-gray-900'} mb-3`}>Need Access?</h3>
              <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-gray-600'} mb-3`}>
                If you believe your organization should have access to this form, please contact your administrator or reach out to our support team with your Tenant ID shown above.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setAuthState('unauthenticated');
                  setUser(null);
                  setError(null);
                }}
                className={`flex-1 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 border-gray-600' : 'bg-white hover:bg-gray-50 border-gray-300'} ${darkMode ? 'text-gray-100' : 'text-gray-700'} font-semibold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2 border`}
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
              <button
                onClick={handleLogout}
                className={`flex-1 ${darkMode ? 'bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800' : 'bg-[#00637d] hover:bg-[#005670]'} text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2`}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        )}

        {/* Authenticating State */}
        {authState === 'authenticating' && (
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-2xl p-12 text-center transition-colors duration-300`}>
            <div className="animate-spin w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>Authenticating...</h2>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Redirecting to Microsoft sign-in...</p>
          </div>
        )}

        {/* Authenticated State - Show Form */}
        {authState === 'authenticated' && user && (
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-2xl overflow-hidden transition-colors duration-300`}>
            {/* User Info Header */}
            <div className={`${darkMode ? 'bg-gradient-to-r from-green-900 to-green-800 border-green-700' : 'bg-green-50 border-green-200'} border-b p-4 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <CheckCircle className={`w-6 h-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                <div>
                  <p className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Signed in as {user.name}</p>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-gray-600'}`}>{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} flex items-center gap-2 text-sm font-medium transition-colors`}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>

            {/* Debug Panel - Shown when debug mode is enabled */}
            {debugMode && (
              <div className={`p-6 ${darkMode ? 'bg-gray-900 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'}`}>
                <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'} border rounded-lg p-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Bug className={`w-5 h-5 ${darkMode ? 'text-cyan-400' : 'text-[#00637d]'}`} />
                    <h4 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Debug Information</h4>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>Authenticated user details:</p>
                  <div className={`text-sm ${darkMode ? 'text-gray-300 bg-gray-900 border-gray-700' : 'text-gray-700 bg-gray-50 border-gray-200'} font-mono border p-4 rounded space-y-2`}>
                    <div className="flex">
                      <span className={`${darkMode ? 'text-cyan-400' : 'text-[#00637d]'} font-semibold w-48`}>Name:</span>
                      <span>{user.name}</span>
                    </div>
                    <div className="flex">
                      <span className={`${darkMode ? 'text-cyan-400' : 'text-[#00637d]'} font-semibold w-48`}>Email:</span>
                      <span>{user.email}</span>
                    </div>
                    <div className="flex">
                      <span className={`${darkMode ? 'text-cyan-400' : 'text-[#00637d]'} font-semibold w-48`}>User Principal Name:</span>
                      <span>{user.userPrincipalName}</span>
                    </div>
                    <div className="flex">
                      <span className={`${darkMode ? 'text-cyan-400' : 'text-[#00637d]'} font-semibold w-48`}>Tenant ID:</span>
                      <span>{user.tenantId}</span>
                    </div>
                    <div className="flex">
                      <span className={`${darkMode ? 'text-cyan-400' : 'text-[#00637d]'} font-semibold w-48`}>User ID:</span>
                      <span>{user.userId}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Embedded Form */}
            <div className={`p-6 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
              <div className="mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>Request a new user account below</h2>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>After submitting the form, a receipt will be sent to the email address {user.email} to confirm your submission.</p>
              </div>

              {/* Cognito Forms Seamless Embed Container */}
              <div
                ref={formContainerRef}
                className={`min-h-[900px] rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
              >
                {!formLoaded && (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// MSAL Configuration for Multi-Tenant (moved outside component)
// Allowed redirect URIs for security validation
const ALLOWED_REDIRECT_URIS = [
  'https://sof.apps.ascendnps.org',
  'http://localhost:3000', // For local development
];

// Validate current URL against allowed redirect URIs
const validateRedirectUri = () => {
  const currentOrigin = window.location.origin;
  if (!ALLOWED_REDIRECT_URIS.includes(currentOrigin)) {
    console.error(`Security Warning: Current origin ${currentOrigin} is not in the allowed redirect URI list`);
    return false;
  }
  return true;
};

// Determine redirect URI based on current environment
const getRedirectUri = () => {
  const currentOrigin = window.location.origin;

  // Validate before returning
  if (!validateRedirectUri()) {
    console.warn('Using fallback redirect URI due to validation failure');
    return ALLOWED_REDIRECT_URIS[0]; // Fallback to production URL
  }

  return currentOrigin;
};

const msalConfig = {
  auth: {
    clientId: '26adc4bb-a9de-4253-aa32-895495e19645',
    authority: 'https://login.microsoftonline.com/common', // 'common' enables multi-tenant
    redirectUri: getRedirectUri(), // Dynamically set and validated redirect URI
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  }
};

export default EntraIDFormAuth;